import { useState } from 'react'
import { buyFromMarket, sellToMarket, getMarketPrices } from '@/economy/api'
import { ITEM_DISPLAY_NAMES, formatFollis } from '@/economy/types'
import { useEconomyStore } from '@/state/useEconomyStore'
import type { WalletInfo, ItemType } from '@/economy/types'
import type { InteractionTarget } from '@/state/useEconomyStore'

interface PanelProps { interaction: InteractionTarget; wallet: WalletInfo | null }

const LIVESTOCK: Array<'sheep' | 'chicken'> = ['sheep', 'chicken']
const MOUNT_COST = 50  // follis to rent a mount

export function StablePanel({ interaction, wallet }: PanelProps) {
  const [tab, setTab] = useState<'livestock' | 'mounts'>('livestock')
  const [selectedItem, setSelectedItem] = useState<ItemType | ''>('')
  const [quantity, setQuantity] = useState(1)
  const [mode, setMode] = useState<'buy' | 'sell'>('buy')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mountRented, setMountRented] = useState(false)
  const villageData = useEconomyStore((s) => s.villageData.get(interaction.villageIndex))
  const updateCargoFromTrade = useEconomyStore((s) => s.updateCargoFromTrade)
  const setMarketPrices = useEconomyStore((s) => s.setMarketPrices)
  const setWallet = useEconomyStore((s) => s.setWallet)

  const prices = villageData?.marketPrices ?? []
  const priceMap = new Map(prices.map((p) => [p.item, p]))
  const cargoMap = new Map(wallet?.cargo ?? [])

  async function handleTrade() {
    if (!selectedItem || interaction.serverId === null) return
    setLoading(true)
    setError(null)
    try {
      if (mode === 'buy') {
        const price = priceMap.get(selectedItem)
        const maxPrice = price ? Math.ceil(price.spot_price * quantity * 1.2) : 999999
        const result = await buyFromMarket(interaction.serverId, selectedItem, quantity, maxPrice)
        updateCargoFromTrade(selectedItem, result.new_item_qty, result.new_follis_balance)
      } else {
        const result = await sellToMarket(interaction.serverId, selectedItem, quantity, 0)
        updateCargoFromTrade(selectedItem, result.new_item_qty, result.new_follis_balance)
      }
      const newPrices = await getMarketPrices(interaction.serverId)
      setMarketPrices(interaction.villageIndex, newPrices)
    } catch (e: any) {
      setError(e.message || 'Trade failed')
    }
    setLoading(false)
  }

  async function handleRentMount() {
    if (!wallet || wallet.follis < MOUNT_COST) return
    setLoading(true)
    setError(null)
    try {
      // Use the heal/service endpoint for mount rental payment
      const { healAtChurch } = await import('@/economy/api')
      const newWallet = await healAtChurch(MOUNT_COST)
      setWallet(newWallet)
      setMountRented(true)
    } catch (e: any) {
      setError(e.message || 'Rental failed')
    }
    setLoading(false)
  }

  const selectedPrice = selectedItem ? priceMap.get(selectedItem) : null
  const estimatedCost = selectedPrice && mode === 'buy' ? Math.ceil(selectedPrice.spot_price * quantity * 1.05) : 0
  const estimatedRevenue = selectedPrice && mode === 'sell' ? Math.floor(selectedPrice.spot_price * quantity * 0.95) : 0

  return (
    <>
      <div className="panel-tabs">
        <button className={`panel-tab ${tab === 'livestock' ? 'panel-tab--active' : ''}`} onClick={() => { setTab('livestock'); setError(null) }}>Livestock</button>
        <button className={`panel-tab ${tab === 'mounts' ? 'panel-tab--active' : ''}`} onClick={() => { setTab('mounts'); setError(null) }}>Mounts</button>
      </div>

      {error && <div className="panel-error">{error}</div>}

      {tab === 'livestock' && (
        <>
          <div className="panel-section">
            <h3 className="panel-section__title">Animal Trade</h3>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <button className={`panel-btn ${mode === 'buy' ? 'panel-btn--primary' : ''}`} onClick={() => { setMode('buy'); setSelectedItem('') }}>Buy</button>
              <button className={`panel-btn ${mode === 'sell' ? 'panel-btn--primary' : ''}`} onClick={() => { setMode('sell'); setSelectedItem('') }}>Sell</button>
            </div>

            {mode === 'buy' && LIVESTOCK.map((item) => {
              const price = priceMap.get(item)
              return (
                <div key={item} className="panel-row" style={{ cursor: 'pointer' }} onClick={() => setSelectedItem(item)}>
                  <span className="panel-row__label" style={{ fontWeight: selectedItem === item ? 700 : 400 }}>
                    {ITEM_DISPLAY_NAMES[item]}
                  </span>
                  <span className="panel-row__value">
                    {price ? `${price.spot_price.toFixed(1)}ƒ — ${Math.floor(price.pool_item_reserve)} available` : 'No market data'}
                  </span>
                </div>
              )
            })}

            {mode === 'sell' && LIVESTOCK.map((item) => {
              const qty = cargoMap.get(item) || 0
              if (qty === 0) return null
              const price = priceMap.get(item)
              return (
                <div key={item} className="panel-row" style={{ cursor: 'pointer' }} onClick={() => { setSelectedItem(item); setQuantity(Math.min(qty, quantity || 1)) }}>
                  <span className="panel-row__label" style={{ fontWeight: selectedItem === item ? 700 : 400 }}>
                    {ITEM_DISPLAY_NAMES[item]} &times;{qty}
                  </span>
                  <span className="panel-row__value">
                    {price ? `${price.spot_price.toFixed(1)}ƒ/ea` : '—'}
                  </span>
                </div>
              )
            })}

            {mode === 'sell' && !LIVESTOCK.some((i) => (cargoMap.get(i) || 0) > 0) && (
              <p className="panel-placeholder">No livestock in cargo.</p>
            )}
          </div>

          {selectedItem && (
            <div className="panel-section">
              <h3 className="panel-section__title">{mode === 'buy' ? 'Purchase' : 'Sell'}</h3>
              <div className="panel-input-row">
                <span style={{ color: '#c4b99a', fontFamily: 'Crimson Text, serif', fontSize: 14 }}>
                  {ITEM_DISPLAY_NAMES[selectedItem]}
                </span>
                <span style={{ color: '#8b7355', fontSize: 12 }}>Qty:</span>
                <input
                  type="number"
                  className="panel-number-input"
                  value={quantity}
                  min={1}
                  max={mode === 'sell' ? (cargoMap.get(selectedItem) || 1) : 50}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                />
              </div>
              <div className="panel-cost">
                &asymp; {mode === 'buy' ? formatFollis(estimatedCost) : `${formatFollis(estimatedRevenue)} revenue`}
              </div>
              <button
                className="panel-btn panel-btn--primary"
                style={{ width: '100%' }}
                disabled={loading || (mode === 'buy' && (!wallet || wallet.follis < estimatedCost)) || (mode === 'sell' && quantity > (cargoMap.get(selectedItem) || 0))}
                onClick={handleTrade}
              >
                {loading ? 'Trading...' : `${mode === 'buy' ? 'Buy' : 'Sell'} ${quantity}× ${ITEM_DISPLAY_NAMES[selectedItem]}`}
              </button>
            </div>
          )}
        </>
      )}

      {tab === 'mounts' && (
        <div className="panel-section">
          <h3 className="panel-section__title">Mount Rental</h3>
          {mountRented ? (
            <p style={{ color: '#6b8b4a', fontFamily: 'Crimson Text, serif', fontSize: 13 }}>
              A horse has been saddled for you. Travel speed increased for this visit.
            </p>
          ) : (
            <>
              <p style={{ color: '#8b7355', fontFamily: 'Crimson Text, serif', fontSize: 13, marginBottom: 8 }}>
                Rent a horse for faster overland travel while visiting this village.
              </p>
              <div className="panel-row">
                <span className="panel-row__label">Horse Rental</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="panel-row__value">{MOUNT_COST}ƒ</span>
                  <button
                    className="panel-btn"
                    disabled={loading || !wallet || wallet.follis < MOUNT_COST}
                    onClick={handleRentMount}
                  >
                    {loading ? 'Renting...' : 'Rent'}
                  </button>
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </>
  )
}
